import { apiService } from "../services/api";
import type { AppDispatch } from "../state/store";
import { showAlertDialog } from "../state/utils/confirmationDialogUtils";

export const SALES_RETURN_PRIOR_WARNING_TITLE = "Warning";

const ROUND_OFF_EPSILON = 0.009;

export type PriorSalesReturnEntry = {
  return_no?: string;
  return_date?: string;
  document_amount?: number | string;
  returned_amount?: number | string;
};

export type SalesReturnInvoiceWarning = {
  vouchers: PriorSalesReturnEntry[];
  has_round_off: boolean;
  round_off_amt: number;
  invoice_no: string;
  invoice_amount: number;
  message: string;
  round_off_message: string;
};

function formatPriorReturnWarningDate(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const datePart = raw.split("T", 1)[0].split(" ", 1)[0];
  const [year, month, day] = datePart.split("-");
  if (year?.length === 4 && month && day) {
    return `${day}/${month}/${year}`;
  }
  return raw;
}

function parseAmount(value: unknown): number {
  const amount = parseFloat(String(value ?? 0));
  return Number.isNaN(amount) ? 0 : amount;
}

function formatPriorReturnLine(
  row: PriorSalesReturnEntry
): string {
  const returnNo = String(row.return_no || "—").trim();
  const returnDate = formatPriorReturnWarningDate(row.return_date);
  const returnedAmount = parseAmount(row.returned_amount);
  let detail = ` • ${returnNo}`;
  if (returnDate) detail += ` — ${returnDate}`;
  if (returnedAmount > 0) {
    detail += ` — ${returnedAmount.toFixed(2)}`;
  }
  return detail;
}

function parseRoundOffAmount(value: unknown): number {
  return parseAmount(value);
}

export function invoiceHasRoundOff(roundOffAmt: unknown): boolean {
  return Math.abs(parseRoundOffAmount(roundOffAmt)) > ROUND_OFF_EPSILON;
}

function formatPriorReturnsMessage(
  vouchers: PriorSalesReturnEntry[],
  invoiceNo = "",
  invoiceAmount = 0
): string {
  if (vouchers.length === 0) return "";
  const header = invoiceNo
    ? `Prior sales return entries exist for this invoice (${invoiceNo}):`
    : "Prior sales return entries exist for this invoice:";
  const lines = [
    header,
    ...vouchers.slice(0, 5).map((row) => formatPriorReturnLine(row)),
  ];
  const extra = vouchers.length - 5;
  if (extra > 0) lines.push(` (+${extra} more)`);
  const total = vouchers.reduce(
    (sum, row) => sum + parseAmount(row.returned_amount),
    0
  );
  if (total > 0) lines.push(`Total returned so far: ${total.toFixed(2)}`);
  let invTotal = parseAmount(invoiceAmount);
  if (invTotal <= 0) {
    invTotal = parseAmount(vouchers[0]?.document_amount);
  }
  if (invTotal > 0) {
    lines.push(`Invoice total amount: ${invTotal.toFixed(2)}`);
  }
  lines.push(
    "Please verify return quantities to avoid duplicate or excess returns."
  );
  return lines.join("\n");
}

function formatRoundOffMessage(
  roundOffAmt: number,
  invoiceNo = ""
): string {
  if (!invoiceHasRoundOff(roundOffAmt)) return "";
  const header = invoiceNo
    ? `Selected invoice has a round off value (${invoiceNo}): ${roundOffAmt.toFixed(2)}`
    : `Selected invoice has a round off value: ${roundOffAmt.toFixed(2)}`;
  return [
    header,
    "Please verify return amounts carefully — round off is not included in line-level returns.",
  ].join("\n");
}

export const isSalesReturnAddContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();
  if (module === "sales_ret_add") return true;
  if (sid === "124") return true;
  return false;
};

export async function fetchSalesReturnInvoiceWarning(
  invoiceId: string,
  excludeReturnId?: string
): Promise<SalesReturnInvoiceWarning> {
  const exclude = excludeReturnId
    ? `?exclude_id=${encodeURIComponent(excludeReturnId)}`
    : "";
  const response = await apiService.get<{
    success?: boolean;
    vouchers?: PriorSalesReturnEntry[];
    has_round_off?: boolean;
    round_off_amt?: number | string;
    invoice_no?: string;
    invoice_amount?: number | string;
    message?: string;
    round_off_message?: string;
  }>(
    `/pabws/module/sales_ret_add/prior_returns/${encodeURIComponent(
      invoiceId
    )}${exclude}`
  );
  const vouchers = Array.isArray(response?.vouchers) ? response.vouchers : [];
  const roundOffAmt = parseRoundOffAmount(response?.round_off_amt);
  const invoiceNo = String(response?.invoice_no || "").trim();
  const invoiceAmount =
    parseAmount(response?.invoice_amount) ||
    parseAmount(vouchers[0]?.document_amount);
  const hasRoundOff =
    typeof response?.has_round_off === "boolean"
      ? response.has_round_off
      : invoiceHasRoundOff(roundOffAmt);
  const priorMessage = formatPriorReturnsMessage(
    vouchers,
    invoiceNo,
    invoiceAmount
  );
  const roundOffMessage =
    String(response?.round_off_message || "").trim() ||
    formatRoundOffMessage(roundOffAmt, invoiceNo);
  const message = [priorMessage, hasRoundOff ? roundOffMessage : ""]
    .filter(Boolean)
    .join("\n\n");

  return {
    vouchers,
    has_round_off: hasRoundOff,
    round_off_amt: roundOffAmt,
    invoice_no: invoiceNo,
    invoice_amount: invoiceAmount,
    message,
    round_off_message: hasRoundOff ? roundOffMessage : "",
  };
}

export async function fetchPriorSalesReturnsForInvoice(
  invoiceId: string,
  excludeReturnId?: string
): Promise<PriorSalesReturnEntry[]> {
  const warning = await fetchSalesReturnInvoiceWarning(
    invoiceId,
    excludeReturnId
  );
  return warning.vouchers;
}

export async function showSalesReturnPriorWarningAlert(
  dispatch: AppDispatch,
  message: string
): Promise<void> {
  await showAlertDialog(dispatch, message, SALES_RETURN_PRIOR_WARNING_TITLE);
}

export async function triggerSalesReturnPriorWarning(
  dispatch: AppDispatch,
  invoiceId: string,
  excludeReturnId: string | undefined,
  cacheRef: { current: string }
): Promise<void> {
  const iid = String(invoiceId || "").trim();
  if (!iid) return;
  // Key only on invoice — excludeReturnId changes after save and must not re-alert.
  const cacheKey = `sr:${iid}`;
  if (cacheRef.current === cacheKey) return;
  cacheRef.current = cacheKey;

  try {
    const warning = await fetchSalesReturnInvoiceWarning(
      iid,
      excludeReturnId
    );
    const hasPrior = warning.vouchers.length > 0;
    if (!hasPrior && !warning.has_round_off) return;

    const message =
      warning.message ||
      [
        formatPriorReturnsMessage(
          warning.vouchers,
          warning.invoice_no,
          warning.invoice_amount
        ),
        warning.round_off_message,
      ]
        .filter(Boolean)
        .join("\n\n");
    if (message) {
      await showSalesReturnPriorWarningAlert(dispatch, message);
    }
  } catch {
    cacheRef.current = "";
  }
}
